import { defineStore } from 'pinia';
import { api } from '../boot/axios';
import { parseAnswer } from '../logic/standardforms';
import { useMessageStore } from './message';
import { useAuthStore } from './auth';
import { isCookieAuthMode } from '../boot/authMode';
import { v4 as uuidv4 } from 'uuid';
import { useIterationStore } from 'stores/iteration';
import { useAttachmentStore } from 'stores/attachment';
import { useAudioNoteStore } from 'stores/audioNotes';
import { useIVRJourneyStore } from 'stores/ivrJourney';

/**
 * Whether someone is signed in, for the case loads below to stop after a
 * logout. Cookie auth keeps no token in storage, so it asks the auth store.
 */
const isSignedIn = () =>
  isCookieAuthMode() ? useAuthStore().isAuthenticated : localStorage.getItem('user-token');

export const useCaseStore = defineStore('case', {
  persist: {
    // loadingCases marks requests in flight. A reload ends them, so a saved entry would block that case's fetch.
    afterRestore: ({ store }) => {
      store.loadingCases = [];
    },
  },

  state: () => ({
    caseDetailsKey: 1,
    caseIsLoading: false,
    casesAreLoading: false,
    batchIsLoading: false,
    cases: [],
    loadingCases: [],
    pendingCasesCount: 1000000,
    systemCategories: [],
    // Pending iterations are iterations which have had a case successfully saved, but the iteration itself has not been saved.
    pendingIterations: [],
  }),

  getters: {
    getActivationById: (state) => (activationID) => {
      for (let c = 0; c < state.cases.length; c++) {
        const thisCase = state.cases[c];
        if (thisCase.activations) {
          for (let a = 0; a < thisCase.activations.length; a++) {
            const thisActivation = thisCase.activations[a];
            if (thisActivation.activationID == activationID) {
              return thisActivation;
            }
          }
        }
      }
    },
    getCaseById: (state) => (caseNumber) => {
      return state.cases.find((patientRecord) => patientRecord.patientRecordID == caseNumber);
    },
    getCaseByActivationId: (state) => (activationID) => {
      for (let c = 0; c < state.cases.length; c++) {
        const thisCase = state.cases[c];
        if (thisCase.activations) {
          for (let a = 0; a < thisCase.activations.length; a++) {
            const thisActivation = thisCase.activations[a];
            if (thisActivation.activationID == activationID) {
              return thisCase;
            }
          }
        }
      }
    },
    getCaseByClientCaseNumber: (state) => (clientCaseNumber) => {
      return state.cases.find(
        (patientRecord) => patientRecord.clientCaseNumber == clientCaseNumber,
      );
    },
    getCasesByDoctorId: (state) => (doctorID) => {
      return state.cases.filter((patientRecord) => patientRecord.doctorID == doctorID);
    },
    getCasesByLocationId: (state) => (locationID) => {
      return state.cases.filter((patientRecord) => patientRecord.clientLocationID == locationID);
    },
    getRecentCasesByPatientId: (state) => (patientID) => {
      return state.cases.filter(
        (patientRecord) =>
          patientRecord.patientRecordStatus === 0 && patientRecord.patientID === patientID,
      );
    },
    getCasesByQuery: (state) => (query) => {
      return state.cases.filter(
        // Get all of the field values from the case
        (patientRecord) =>
          Object.values(patientRecord)
            // Filter them to only the text values (drop ID's and booleans)
            .filter((value) => typeof value === 'string')
            // Filter the text values for the search string
            .filter(
              (value) => value.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) >= 0,
              // When there's more than one matching string, we have a matching case
            ).length > 0 || ('' + patientRecord.patientRecordID).indexOf(query) !== -1,
      );
    },
    getUnreadCount: (state) => {
      let unread = 0;
      for (let c = 0; c < state.cases.length; c++) {
        if (state.cases[c].patientRecordStatus === 0) {
          unread += 0; // this.getUnreadCountForCase(state.cases[c])
        }
      }
      return unread;
    },
    getUnreadCountForCase: () => (patientRecord) => {
      // We don't report unread status for closed cases.
      if (patientRecord.patientRecordStatus !== 0) {
        return 0;
      }
      let unread = 0;
      if (patientRecord.activations) {
        for (let a = 0; a < patientRecord.activations.length; a++) {
          const activation = patientRecord.activations[a];
          unread += activation.activationMessagingUnreadCount;
        }
      }
      if (patientRecord.attachments) {
        for (let a = 0; a < patientRecord.attachments.length; a++) {
          const attachment = patientRecord.attachments[a];
          // We're going to make the assumption (that is mostly true) that Activation attachments are already counted above
          if (!attachment.isRead && attachment.attachmentSource === 'PatientRecord') {
            unread++;
          }
        }
      }
      if (patientRecord.iterations) {
        for (let i = 0; i < patientRecord.iterations.length; i++) {
          if (!patientRecord.iterations[i].isRead) {
            unread++;
          }
        }
      }
      if (patientRecord.standardFormAnswers) {
        for (let i = 0; i < patientRecord.standardFormAnswers.length; i++) {
          if (!patientRecord.standardFormAnswers[i].isRead) {
            unread++;
          }
        }
      }
      return unread;
    },
    getPendingCases: (state) => {
      state.pendingCases = state.cases.filter((currentCase) => currentCase.isTemporary);
      return state.pendingCases;
    },
    isCaseAvailable: (state) => (patientRecordID) => {
      for (let i = 0; i < state.cases.length; i++) {
        const thisCase = state.cases[i];
        if (thisCase.patientRecordID === patientRecordID) {
          return true;
        }
      }
      return false;
    },
    getCaseLocations: (state) => {
      return [...new Set(state.cases.map((item) => item.locationName).sort())];
    },
    /**
     * Returns all related cases for a given case ID.
     * Includes: parent, children, siblings, and all cases in linkedRecords.
     * @param caseId - The patient record ID to find related cases for
     * @returns Array of related case records
     */
    getRelatedCases: (state) => (caseId) => {
      const thisCase = state.cases.find((c) => c.patientRecordID == caseId);
      if (!thisCase) {
        return [];
      }

      const linkedCaseIds = new Set();
      const currentCaseId = thisCase.patientRecordID;
      const parentId = thisCase.parentPatientRecordID;

      // Add cases from this case's linkedRecords
      if (thisCase.linkedRecords && thisCase.linkedRecords.length > 0) {
        thisCase.linkedRecords.forEach((id) => linkedCaseIds.add(id));
      }

      // Add parent case if this case is a child
      if (parentId) {
        linkedCaseIds.add(parentId);

        // Get the parent case to access all of its linkedRecords
        const parentCase = state.cases.find((c) => c.patientRecordID == parentId);
        if (parentCase?.linkedRecords && parentCase.linkedRecords.length > 0) {
          parentCase.linkedRecords.forEach((id) => {
            if (id !== currentCaseId) {
              linkedCaseIds.add(id);
            }
          });
        }
      }

      // Single iteration: find siblings (same parent) and children (this case is parent)
      state.cases.forEach((c) => {
        // Add siblings (other cases with the same parent)
        if (
          parentId &&
          c.parentPatientRecordID === parentId &&
          c.patientRecordID !== currentCaseId
        ) {
          linkedCaseIds.add(c.patientRecordID);
        }
        // Add children (cases that have this case as their parent)
        if (c.parentPatientRecordID === currentCaseId) {
          linkedCaseIds.add(c.patientRecordID);
        }
      });

      // Map IDs to case records
      return Array.from(linkedCaseIds)
        .map((id) => state.cases.find((c) => c.patientRecordID == id))
        .filter((record) => record !== undefined);
    },
  },

  actions: {
    async toggleTestFlag(patientRecordID) {
      try {
        await api.put(`api/v2/cases/${patientRecordID}/toggle-test`);
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    async getLinkableCases(patientRecordID, includeTest) {
      try {
        const res = await api.get(
          `api/v2/cases/${patientRecordID}/linkable?includeTest=${includeTest}`,
        );
        return res.data;
      } catch (err) {
        console.error(err);
      }
    },
    async getPatientHistory(patientID) {
      try {
        const res = await api.get(`api/v2/cases/${patientID}/history`);
        return res.data;
      } catch (err) {
        console.error(err);
      }
    },
    saveCaseLocally(newCase) {
      // Check to see if the new case is already present
      let present = false;
      // Now try to save the new case
      if (this.cases) {
        for (let i = 0; i < this.cases.length; i++) {
          if (this.cases[i].patientRecordID < newCase.patientRecordID) {
            // The incoming case is newer than the current one, insert it at this location
            this.cases.splice(i, 0, newCase);
            present = true;
            break;
          } else if (this.cases[i].patientRecordID === newCase.patientRecordID) {
            // found them, add in the new values
            const oldCase = this.cases[i];
            // remove the preview flag
            oldCase.isPreview = false;
            if (
              oldCase.iterations &&
              !newCase.iterations &&
              oldCase.iterations.some((iteration) => iteration.GUID)
            ) {
              // A summary has no iterations to merge into. Keep the old details
              // so iterations in progress are not lost.
              newCase.iterations = oldCase.iterations;
              newCase.activations = oldCase.activations;
            } else if (oldCase.iterations && newCase.iterations) {
              // Check for iterations in progress in the old case
              for (let n = 0; n < oldCase.iterations.length; n++) {
                const oldIteration = oldCase.iterations[n];
                if (oldIteration.GUID) {
                  // Iterations in progress have a GUID
                  // Check to see if the iteration is in the new case
                  // Once an iteration is submitted - it CANNOT be edited
                  // So the server's version is the only one that matters
                  if (
                    !newCase.iterations.some((i) => i.GUID === oldIteration.GUID) &&
                    !newCase.iterations.some(
                      (i) => i.patientRecordIterationID === oldIteration.patientRecordIterationID,
                    )
                  ) {
                    // If there is no iteration that matches the GUID AND there is no iteration that matches the iterationID
                    newCase.iterations.push(oldIteration);
                  }
                }
              }
            }
            if (oldCase.linkedRecords && oldCase.linkedRecords.length > 0) {
              if (!newCase.linkedRecords) {
                newCase.linkedRecords = oldCase.linkedRecords;
              } else {
                oldCase.linkedRecords.forEach((oldLinkedRecord) => {
                  if (!newCase.linkedRecords.some((r) => r === oldLinkedRecord)) {
                    newCase.linkedRecords.push(oldLinkedRecord);
                  }
                });
              }
            }
            if (newCase.activations) {
              newCase.newCallsCount = newCase.activations.reduce((acc, current) => {
                if (current.activationMessagingFlag === 0 && current.status === 0) {
                  acc += 1;
                }
                return acc;
              }, 0);
            }
            if (newCase.isClosed === undefined && newCase.patientRecordStatus !== undefined) {
              newCase.isClosed = newCase.patientRecordStatus !== 0;
            }
            this.cases[i] = newCase;
            // Object.assign(this.cases[i], newCase)
            present = true;
            break;
          }
        }
      }
      // If it is offline case increment pending cases count
      if (newCase.isTemporary === true) {
        this.pendingCasesCount = this.pendingCasesCount + 1;
      }
      // If not already present, add them to the end of our store.
      if (!present) {
        this.cases.push(newCase);
      }
      this.caseIsLoading = false;
    },
    saveCasesLocally(newCases) {
      if (!this.cases || this.cases.length === 0 || newCases.length > 30) {
        // When there are 100's the performance is terrible.
        // Setting the limit to 30 so that most telemed clients can still merge.
        // When doing the initial load, we don't have to check for existing data, we'll just save it in bulk.
        this.cases = newCases;
      } else {
        // Save one at a time to merge if necessary
        for (let i = 0; i < newCases.length; i++) {
          // this.commit(CASE_GET_CASE_SUCCESS, newCases[i])
          this.saveCaseLocally(newCases[i]);
        }
      }
      this.casesAreLoading = false;
    },
    deleteCaseLocally(patientRecordID) {
      var removeIndex = this.cases
        .map(function (item) {
          return item.patientRecordID;
        })
        .indexOf(patientRecordID);
      this.cases.splice(removeIndex, 1);
      this.pendingCasesCount = this.pendingCasesCount - 1;
    },
    saveActivationLocally(activation) {
      caseLoop: for (let c = 0; c < this.cases.length; c++) {
        const theCase = this.cases[c];
        if (theCase.activations) {
          for (let a = 0; a < theCase.activations.length; a++) {
            if (activation.activationID === theCase.activations[a].activationID) {
              const theCase = this.cases[c];
              Object.assign(this.cases[c].activations[a], activation);
              Object.assign(this.cases[c], theCase);
              break caseLoop;
            }
          }
        }
      }
    },
    /**
     * Saves a new iteration to the store. If the iteration already exists, it will be updated.
     * This will modify the related case in the store in-place.
     * @param iteration
     * @returns {void}
     */
    saveIterationNotesLocally(iteration) {
      const theCase = this.cases.find((c) => c.patientRecordID === iteration.patientRecordID);
      if (!theCase) {
        // If the case is not in the store, we can't save the iteration notes
        console.error('Cannot find case - cannot save iteration notes');
        return;
      }
      if (iteration.patientRecordIterationID === undefined && iteration.GUID === undefined) {
        // This is a new iteration
        iteration.GUID = uuidv4();
        iteration.iterationDateCreated = new Date().getTime();
      }
      // If there are already iterations in the case, find the one we want to update and update it
      if (
        theCase.iterations !== undefined &&
        theCase.iterations !== null &&
        theCase.iterations.length > 0
      ) {
        const oldIteration = theCase.iterations.find((i) => {
          // If the iteration has a patientRecordIterationID, we can use that to find it
          if (
            iteration.patientRecordIterationID !== undefined &&
            iteration.patientRecordIterationID === i.patientRecordIterationID
          ) {
            return true;
            // If the iteration doesn't have a patientRecordIterationID, we can use the GUID to find it
          } else if (iteration.GUID !== undefined && iteration.GUID === i.GUID) {
            return true;
          }
          return false;
        });
        if (oldIteration === undefined) {
          // The iteration is new
          theCase.iterations.push(iteration); // Add the iteration to the case
        } else {
          // The iteration already exists
          // Update the case to reflect the updated iteration
          theCase.transportRecommendation = iteration.transportRecommendation;
          theCase.callerRating = iteration.callerRating;
          theCase.callerRating2 = iteration.callerRating2;
          theCase.OHCanReturnToWork = iteration.OHCanReturnToWork;
          theCase.OHCanDoFullDuties = iteration.OHCanDoFullDuties;

          theCase.iterations = theCase.iterations.map((i) => {
            if (
              i.GUID === iteration.GUID ||
              i.patientRecordIterationID === iteration.patientRecordIterationID
            ) {
              return iteration;
            }
            return i;
          });
        }
      } else {
        // There are no iterations in the case yet
        theCase.iterations = []; // Create the iterations array
        theCase.iterations.push(iteration); // Add the iteration to the case
      }
    },
    async resendReports(record) {
      const res = await api.get(`/api/v2/cases/${record.patientRecordID}/resend`);
      return res.data;
    },
    /**
     * Iterates through all cases and their iterations in the store. If any an iteration has a GUID which matches
     * the provided GUID, it will be removed from the store.
     * @param iterationGUID
     */
    deleteIterationByGUID(iterationGUID) {
      const theCase = this.cases.find(
        (c) => c.iterations !== undefined && c.iterations.find((i) => i.GUID === iterationGUID),
      );
      if (theCase) {
        theCase.iterations = theCase.iterations.filter((i) => i.GUID !== iterationGUID);
      }
    },
    /**
     * Iterates through the cases in the store. Attempts to find a case with the given iteration
     * If found, remove the iteration from the case.
     * @param iteration The iteration to remove
     */
    deleteIterationNotesLocally(iteration) {
      if (iteration.patientRecordIterationID < 0 || iteration.GUID) {
        const theCase = this.cases.find(
          (c) =>
            iteration.caseNumber === c.patientRecordID ||
            iteration.patientRecordID === c.patientRecordID,
        );
        if (theCase != undefined) {
          if (iteration.GUID != undefined) {
            theCase.iterations = theCase.iterations.filter((i) => i.GUID !== iteration.GUID);
          } else {
            theCase.iterations = theCase.iterations.filter(
              (i) => i.patientRecordIterationID !== iteration.patientRecordIterationID,
            );
          }
        }
      }
    },
    /**
     * This is used to save changes to an iteration, typically only its read status.  The old EMwerx would sometimes have
     * an iteration that was saved to a linked case and not the master case, so we can't find the case here based on the
     * caseNumber in the iteration.  Instead we have to do a full search for the iteration by iteration ID through all
     * the cases.
     */
    saveIterationLocally(iteration) {
      // Loop through the cases
      for (let c = 0; c < this.cases.length; c++) {
        const theCase = this.cases[c];
        // Loop through the iterations in the case
        for (let i = 0; i < theCase.iterations.length; i++) {
          // See if this is the iteration we're looking for
          if (
            theCase.iterations[i].patientRecordIterationID === iteration.patientRecordIterationID
          ) {
            Object.assign(theCase.iterations[i], iteration);
            Object.assign(this.cases[c], theCase);
            return;
          }
        }
      }
    },
    saveQualityReviewLocally(qualityReview) {
      for (let i = 0; i < this.cases.length; i++) {
        if (qualityReview.patientRecordID === this.cases[i].patientRecordID) {
          this.cases[i].qualityReviews.push(qualityReview);
          break;
        }
      }
    },
    saveQualityRecordLocally(qualityRecord) {
      caseLoop: for (let i = 0; i < this.cases.length; i++) {
        if (qualityRecord.emwerxRecordNumber === this.cases[i].patientRecordID) {
          // Check if it's already here and can be updated
          for (let j = 0; j < this.cases[i].qualityRecords.length; j++) {
            if (this.cases[i].qualityRecords[j].qualityRecordID === qualityRecord.qualityRecordID) {
              // found them, add in the new values
              Object.assign(this.cases[i].qualityRecords[j], qualityRecord);
              break caseLoop;
            }
          }
          // Didn't find it, add it to the end.
          this.cases[i].qualityRecords.push(qualityRecord);
          break;
        }
      }
    },
    savePrescriptionLocally(rx) {
      for (let c = 0; c < this.cases.length; c++) {
        if (this.cases[c].patientRecordID === rx.caseNumber) {
          for (let d = 0; d < this.cases[c].iterations.length; d++) {
            if (
              this.cases[c].iterations[d].patientRecordIterationID === rx.patientRecordIterationID
            ) {
              for (let e = 0; e < this.cases[c].iterations[d].medications.length; e++) {
                if (
                  this.cases[c].iterations[d].medications[e].patientRecordMedicationID ===
                  rx.patientRecordMedicationID
                ) {
                  this.cases[c].iterations[d].medications[e] = rx;
                }
              }
            }
          }
          for (let m = 0; m < this.cases[c].medications.length; m++) {
            if (
              this.cases[c].medications[m].patientRecordMedicationID ===
              rx.patientRecordMedicationID
            ) {
              this.cases[c].medications[m] = rx;
            }
          }
        }
      }
    },
    deletePrescriptionLocally(medication) {
      for (let c = 0; c < this.cases.length; c++) {
        if (this.cases[c].patientRecordID === medication.patientRecordID) {
          for (let d = 0; d < this.cases[c].iterations.length; d++) {
            if (
              this.cases[c].iterations[d].patientRecordIterationID ===
              medication.patientRecordIterationID
            ) {
              for (let e = 0; e < this.cases[c].iterations[d].medications.length; e++) {
                if (
                  this.cases[c].iterations[d].medications[e].patientRecordMedicationID ===
                  medication.patientRecordMedicationID
                ) {
                  this.cases[c].iterations[d].medications[e].medicationStatus = 1;
                }
              }
            }
          }
        }
      }
    },
    saveFormLocally(newForm) {
      // The incoming form can be either a blank form about to be completed, or a completed form coming from the db.
      // If it is a blank form it will need the standardFormAnswerID and standardFormAnswerDate fields in order to
      // render properly in the timeline.
      if (newForm.standardFormAnswerID === undefined) {
        const timestamp = new Date().getTime();
        newForm.standardFormAnswerDate = timestamp;
        newForm.standardFormAnswerID = -timestamp;
      }
      // The answer is stored as a JSON string in the database. We want to parse it if that's the case in the incoming form.
      parseAnswer(newForm);
      const _savableForm = JSON.parse(JSON.stringify(newForm));

      caseLoop: for (let c = 0; c < this.cases.length; c++) {
        const theCase = this.cases[c];
        if (theCase.patientRecordID === _savableForm.patientRecordID) {
          const newCase = JSON.parse(JSON.stringify(theCase));
          // Not all cases will contain StandardFormAnswers.
          if (theCase.standardFormAnswers) {
            // Check to see if it is already there
            for (let f = 0; f < theCase.standardFormAnswers.length; f++) {
              // Standard forms can be unfilled or filled.
              // We don't want more than one incomplete form per case.
              // Incomplete forms are identified by having a negative ID
              if (
                _savableForm.standardFormAnswerID ===
                theCase.standardFormAnswers[f].standardFormAnswerID
              ) {
                newCase.standardFormAnswers[f] = _savableForm;
                Object.assign(this.cases[c], newCase);
                break caseLoop;
              }
            }
            // Didn't find the form, so we'll add it.
            newCase.standardFormAnswers.push(_savableForm);
            Object.assign(this.cases[c], newCase);
            break;
          } else {
            newCase.standardFormAnswers = [_savableForm];
            Object.assign(this.cases[c], newCase);
            break;
          }
        }
      }
    },
    deleteFormLocally(formToDelete) {
      // We will only delete a form that is partially completed.  Once it's saved to the db we keep it.
      if (formToDelete.standardFormAnswerID < 0) {
        for (let c = 0; c < this.cases.length; c++) {
          const theCase = this.cases[c];
          if (theCase.patientRecordID === formToDelete.patientRecordID) {
            if (theCase.standardFormAnswers) {
              for (let f = 0; f < theCase.standardFormAnswers.length; f++) {
                if (
                  formToDelete.standardFormAnswerID ===
                  theCase.standardFormAnswers[f].standardFormAnswerID
                ) {
                  const newCase = JSON.parse(JSON.stringify(theCase));
                  newCase.standardFormAnswers.splice(f, 1);
                  Object.assign(this.cases[c], newCase);
                  break;
                }
              }
            }
            break;
          }
        }
      }
    },
    /**
     * It is possible for a medication to be created offline, and for a case to be created offline, and then for that
     * new medication to be added to the new case.  When this happens, we will have to update the id of the medications
     * after they are eventually saved to the database.
     * @param options Eg: { from: -18277343, to: 94833 }
     */
    /* No longer needed with the new API V2 sync methods
    [MEDICATION_UPDATE_ID] (options) {
      for (let c = 0; c < state.cases.length; c++) {
        const theCase = state.cases[c]
        if (theCase.medications) {
          for (let m = 0; m < theCase.medications.length; m++) {
            const medication = theCase.medications[m]
            if (medication.medicationId === options.from) {
              medication.medicationId = options.to
            }
          }
        }
        if (theCase.iterations) {
          for (let i = 0; i < theCase.iterations.length; i++) {
            const iteration = theCase.iterations[i]
            for (let m = 0; m < iteration.medications.length; m++) {
              const medication = iteration.medications[m]
              if (medication.medicationId === options.from) {
                medication.medicationId = options.to
              }
            }
          }
        }
      }
    },
     */
    /* Duplicate of saveFormLocally above?
    [CASE_PUT_STANDARD_FORM_ANSWER] (state, form) {
      for (let c = 0; c < state.cases.length; c++) {
        const theCase = state.cases[c]
        if (theCase.patientRecordID !== form.patientRecordID) {
          continue
        }
        if (theCase.standardFormAnswers && theCase.standardFormAnswers.length > 0) {
          Object.assign(state.cases[c].standardFormAnswers[theCase.standardFormAnswers.length], form)
        } else {
          Object.assign(state.cases[c].standardFormAnswers, [form])
        }
        Object.assign(state.cases[c], theCase)
      }
    },
     */

    async dataRefresh() {
      await this.fetchSystemCategories();
      await this.fetchCases();
    },
    deletePrescription(medication) {
      return new Promise((resolve, reject) => {
        api
          .delete(
            'api/v1/cases/' +
              medication.patientRecordID +
              '/medications/' +
              medication.patientRecordMedicationID,
          )
          .then((response) => {
            // commit(CASE_DELETE_PRESCRIPTION_SUCCESS, medication)
            this.deletePrescriptionLocally(medication);
            resolve(response);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      });
    },
    async fetchLinkedCases(patientRecord) {
      if (!patientRecord || !patientRecord.linkedRecords) {
        return;
      }
      await Promise.all(
        patientRecord.linkedRecords.map(async (linkedRecord) => {
          this.fetchCase(linkedRecord, false);
        }),
      );
    },
    fetchCase(patientRecordID, getLinkedCases = false) {
      const iterationStore = useIterationStore();
      const attachmentStore = useAttachmentStore();
      const audioNoteStore = useAudioNoteStore();

      // Only fetch if we have a valid patientRecordID
      if (patientRecordID) {
        // Callers pass the ID as a number or a string, so compare as strings
        const loadingKey = String(patientRecordID);
        // Don't fetch if it's already being fetched (sometimes notifications cause repeated fetch)
        if (!this.loadingCases.includes(loadingKey)) {
          iterationStore.fetchCaseIterations(patientRecordID);
          return new Promise((resolve, reject) => {
            this.caseIsLoading = true;
            console.log(`adding ${patientRecordID} to loading`);
            this.loadingCases.push(loadingKey);
            api
              .get('/api/v1/cases/' + patientRecordID)
              .then((response) => {
                this.saveCaseLocally(response.data);
                attachmentStore.fetchCaseAttachments(response.data.patientRecordGUID);
                audioNoteStore.fetchCaseAudioNotes(response.data.patientRecordGUID);
                if (getLinkedCases) {
                  this.fetchLinkedCases(response.data);
                }
                for (let a = 0; a < response.data.activations.length; a++) {
                  const messageStore = useMessageStore();
                  messageStore.fetchMessages(response.data.activations[a].activationID);

                  const ivrJourneyStore = useIVRJourneyStore();
                  ivrJourneyStore.fetchJourneysByActivationID(
                    response.data.activations[a].activationID,
                  );
                }
                resolve(response.data);
              })
              .catch((err) => {
                console.error(err);
                reject(err);
              })
              .finally(() => {
                this.caseIsLoading = false;
                console.log(`removing ${patientRecordID} from loading`);
                this.loadingCases = this.loadingCases.filter((c) => c !== loadingKey);
              });
          });
        }
      }
    },
    async linkCases(caseLinkDTO) {
      const res = await api.post('api/v2/cases/link', caseLinkDTO);
      if (res.status === 401) {
        throw new Error('unauthorized');
      } else if (res.status === 400) {
        throw new Error('bad request');
      } else if (res.status === 500) {
        throw new Error('server error');
      }
      // Optimistically update local store with the new link relationship
      this.updateLinkedCasesLocally(caseLinkDTO.patientRecordID, caseLinkDTO.parentPatientRecordID);
    },
    /**
     * Updates the local store to reflect a new link between two cases.
     * This ensures the UI updates immediately without waiting for a fresh API fetch.
     * @param childCaseID - The ID of the child case being linked
     * @param parentCaseID - The ID of the parent case
     */
    updateLinkedCasesLocally(childCaseID, parentCaseID) {
      // Build index map for O(1) lookups instead of repeated findIndex calls
      const caseIndexMap = new Map();
      this.cases.forEach((c, index) => caseIndexMap.set(c.patientRecordID, index));

      // Get the parent case to access ALL of its linkedRecords
      const parentIndex = caseIndexMap.get(parentCaseID);
      const parentCase = parentIndex !== undefined ? this.cases[parentIndex] : null;
      const parentLinkedRecords = parentCase?.linkedRecords || [];

      // Find existing siblings (other cases with the same parent)
      const siblingCaseIds = this.cases
        .filter(
          (c) => c.parentPatientRecordID === parentCaseID && c.patientRecordID !== childCaseID,
        )
        .map((c) => c.patientRecordID);

      // Collect all related case IDs from the parent's network
      const allRelatedCaseIds = new Set([parentCaseID, ...parentLinkedRecords, ...siblingCaseIds]);
      // Remove the child case itself from the set
      allRelatedCaseIds.delete(childCaseID);

      // Find and update the child case
      const childCaseIndex = caseIndexMap.get(childCaseID);
      if (childCaseIndex !== undefined) {
        const childCase = { ...this.cases[childCaseIndex] };
        childCase.parentPatientRecordID = parentCaseID;
        // Add ALL related cases to child's linkedRecords
        if (!childCase.linkedRecords) {
          childCase.linkedRecords = [];
        }
        const newLinkedRecords = new Set([...childCase.linkedRecords, ...allRelatedCaseIds]);
        childCase.linkedRecords = Array.from(newLinkedRecords);
        this.cases[childCaseIndex] = childCase;
      }

      // Find and update the parent case
      if (parentIndex !== undefined) {
        const updatedParentCase = { ...this.cases[parentIndex] };
        if (!updatedParentCase.linkedRecords) {
          updatedParentCase.linkedRecords = [];
        }
        if (!updatedParentCase.linkedRecords.includes(childCaseID)) {
          updatedParentCase.linkedRecords = [...updatedParentCase.linkedRecords, childCaseID];
        }
        this.cases[parentIndex] = updatedParentCase;
      }

      // Update all related cases to include the new child in their linkedRecords
      allRelatedCaseIds.forEach((relatedId) => {
        if (relatedId === parentCaseID) return; // Already updated parent above
        const relatedIndex = caseIndexMap.get(relatedId);
        if (relatedIndex !== undefined) {
          const relatedCase = { ...this.cases[relatedIndex] };
          if (!relatedCase.linkedRecords) {
            relatedCase.linkedRecords = [];
          }
          if (!relatedCase.linkedRecords.includes(childCaseID)) {
            relatedCase.linkedRecords = [...relatedCase.linkedRecords, childCaseID];
          }
          this.cases[relatedIndex] = relatedCase;
        }
      });
    },
    async fetchCasesByQuery(query) {
      const response = await api.get('api/v2/cases/query', {
        params: {
          query: query,
        },
      });
      try {
        this.saveCasesLocally(response.data);
      } catch (err) {
        console.error('error getting cases query: ' + err);
        this.casesAreLoading = false;
      }
    },
    async advancedSearch(criteria) {
      this.casesAreLoading = true;
      if (!isSignedIn()) {
        return;
      }
      const res = await api.post(`api/v2/cases/advanced-search`, criteria);
      return res.data;
    },
    async fetchCases() {
      this.casesAreLoading = true;
      this.batchIsLoading = true;
      if (!isSignedIn()) {
        return;
      } // prevents case loading after user has signed out
      try {
        const response = await api.get('/api/v2/cases');
        if (!isSignedIn()) {
          return;
        } // prevents case loading after user has signed out
        const caseSummaries = response.data.map((patientRecord) => {
          return { ...patientRecord, isPreview: true };
        });
        this.saveCasesLocally(caseSummaries);
        this.batchIsLoading = false;
      } catch (err) {
        console.error('error getting cases: ' + err);
        this.casesAreLoading = false;
        this.batchIsLoading = false;
      }
    },
    postCase(patientRecordDto) {
      return new Promise((resolve, reject) => {
        // commit(CASE_GET_CASE_PENDING)
        this.caseIsLoading = true;
        // Need to clear some fields that the API doesn't want to see.
        patientRecordDto.lastReadDate = undefined;
        patientRecordDto.isPreview = undefined;
        patientRecordDto.standardFormAnswers = [];
        if (patientRecordDto.iterations) {
          patientRecordDto.iterations.forEach((iter) => {
            delete iter.pending;
            delete iter.GUID;
          });
        }
        api
          .put('api/v1/cases/' + patientRecordDto.patientRecordID, patientRecordDto)
          .then((response) => {
            // commit(CASE_GET_CASE_SUCCESS, response.data)
            this.saveCaseLocally(response.data);
            resolve(response);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          })
          .finally(() => {
            this.caseIsLoading = false;
          });
      });
    },
    fetchCaseReport(patientRecordID) {
      return new Promise((resolve, reject) => {
        api({
          url: 'api/v1/documents/casereport/' + patientRecordID,
          method: 'GET',
          responseType: 'blob',
        })
          .then((response) => {
            resolve(response);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      });
    },
    fetchPrescriptionReport(prescription) {
      return new Promise((resolve, reject) => {
        api({
          url: 'api/v1/documents/prescriptionreport/' + prescription.caseNumber,
          method: 'POST',
          responseType: 'blob',
          data: prescription.prescriptionDetails,
        })
          .then((response) => {
            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    /**
     * POST the iteration to the API endpoint. If the API call fails, it will be saved to the local store and marked
     * as pending. If the API call succeeds, the local store will be updated with the new iteration.
     * @param iteration
     * @returns {Promise<void>}
     */
    async postIterationNotes(iteration) {
      const copiedIteration = JSON.parse(JSON.stringify(iteration)); // Deep copy
      const caseNumber = iteration.caseNumber || iteration.patientRecordID;
      const iterationGUID = iteration.GUID;
      delete copiedIteration.GUID;
      delete copiedIteration.pending;
      const postPath = `api/v1/cases/${caseNumber}/iterations`;
      try {
        const res = await api.post(postPath, copiedIteration);
        this.saveIterationNotesLocally(res.data);
        if (iterationGUID) {
          this.deleteIterationByGUID(iterationGUID);
        }
      } catch (err) {
        // If the iteration failed, we need to re-assign the GUID and mark it as pending
        copiedIteration.pending = true;
        copiedIteration.GUID = iterationGUID;
        this.saveIterationNotesLocally(copiedIteration);
        throw err;
      }
    },
    postQualityReview(qualityReview) {
      return new Promise((resolve, reject) => {
        api
          .post('api/v1/qualityreview', qualityReview)
          .then((response) => {
            // commit(CASE_POST_QUALITY_REVIEW_SUCCESS, response.data)
            this.saveQualityReviewLocally(response.data);
            resolve(response);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      });
    },
    postQualityRecord(qualityRecord) {
      return new Promise((resolve, reject) => {
        if (qualityRecord.qualityRecordID) {
          api
            .put('api/v1/qualityrecords', qualityRecord)
            .then((response) => {
              // commit(CASE_PUT_QUALITY_RECORD_SUCCESS, response.data)
              this.saveQualityRecordLocally(response.data);
              this.fetchCase(response.data.emwerxRecordNumber);
              resolve(response.data);
            })
            .catch((err) => {
              console.error(err);
              reject(err);
            });
        } else {
          api
            .post('api/v1/qualityrecords', qualityRecord)
            .then((response) => {
              // commit(CASE_POST_QUALITY_RECORD_SUCCESS, response.data)
              this.saveQualityRecordLocally(response.data);
              this.fetchCase(response.data.emwerxRecordNumber);
              resolve(response);
            })
            .catch((err) => {
              console.error(err);
              reject(err);
            });
        }
      });
    },
    async reAssignHCP(patientRecord, hcpEmployeeID, reason, reassignEmailFlag) {
      const dto = {
        patientRecordID: patientRecord,
        doctorID: hcpEmployeeID,
        doctorReassignReason: reason,
        doctorReassignEmailFlag: reassignEmailFlag,
      };
      await this.postCase(dto);
    },
    async reOpenCase(theCase) {
      const clone = JSON.parse(JSON.stringify(theCase));
      clone.patientRecordStatus = 0;
      await this.postCase(clone);
    },
    async fetchSystemCategories() {
      api
        .get('api/v1/systemcategories')
        .then((response) => {
          const categories = response.data.map((cat) => cat.systemCategoryName);
          this.systemCategories = categories;
          return categories;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    async updateCaseRating(patientRecordID, updateRating) {
      await api.put(`/api/v2/cases/${patientRecordID}/rating`, updateRating);
    },
    /** Iterates through cases, finds cases with pending iterations and syncs them with the server.
     If a case has a pending iteration, it will send all pending iterations for that case.
     Then it will re-fetch the case to ensure the local copy is up-to-date.
     @returns {Promise}
     */
    async syncIterations() {
      // Iterate through the cases and sync the iterations
      for (let i = 0; i < this.cases.length; i++) {
        const theCase = this.cases[i];
        if (theCase.isTemporary) {
          // If the case is temporary, we can't sync it.
          // We will do this instead inside of activation sync.
          continue;
        }
        let flag = false; // This flag is used to determine if we need to update the case.
        if (theCase.iterations && theCase.iterations.length > 0) {
          // Note that we are iterating using a for loop instead of a forEach.
          // This is because we are using an async function inside the loop.
          // We need to use a for loop so that we can use await.
          for (let j = 0; j < theCase.iterations.length; j++) {
            const iteration = theCase.iterations[j];
            if (iteration.pending) {
              flag = true; // We found a pending iteration, so we need to update the case.
              try {
                await this.postIterationNotes(iteration);
              } catch (err) {
                console.error(
                  `Failed to sync pending iteration for case ${theCase.patientRecordID}`,
                  err,
                );
              }
            }
          }
        }
        if (flag) {
          // If we found a pending iteration, we need to update the case.
          // Note that we waited until we were done syncing iterations to do this.
          await this.fetchCase(theCase.patientRecordID);
        }
      }
    },
    sync() {
      this.syncIterations();
    },
  },
});
